import styled, { css, keyframes } from 'styled-components'
import {
	backgroundOrange,
	vanilla,
	darkOrange,
	orange,
	blue,
	gold,
	success,
	red,
	danger,
	blackOrange,
	black,
	textMuted,
	darkGrey,
	white,
	grey,
	border1,
	border2,
} from '@/theme/colors'
import { fontMono } from '@/theme/fonts'
import { shadows, radii, media, motion } from '@/theme/tokens'
import { fontSizes, text, leading } from '@/theme/typography'
import { dashedSlot } from '@/theme/recipes'
import { Button } from '@components/Button'
import { Textarea } from '@components/Field'

const fadeSlideUp = keyframes`
	from { opacity: 0; transform: translateY(8px); }
	to   { opacity: 1; transform: translateY(0); }
`

const pulseDot = keyframes`
	0% { opacity: 1; }
	100% { opacity: 0.55; }
`

const orbitSpin = keyframes`
	from { transform: rotate(0deg); }
	to   { transform: rotate(360deg); }
`

const orbitSpinReverse = keyframes`
	from { transform: rotate(360deg); }
	to   { transform: rotate(0deg); }
`

const stagger = keyframes`
	from { opacity: 0; transform: translateY(4px); }
	to   { opacity: 1; transform: translateY(0); }
`






export const ContentCard = styled.div`
	width: 100%;
	background: ${white};
	border-radius: ${radii.card};
	border: 1px solid ${border1};
	box-shadow: ${shadows.card};
	overflow: hidden;
	animation: ${fadeSlideUp} 200ms ease-out;
	display: flex;
	flex-direction: column;
`

export const CardBody = styled.div`
	padding: 20px 24px 4px;
	flex: 1;
	min-height: 0;

	${media.phone} {
		padding: 16px 14px 4px;
	}
`

export const CardBodyCentered = styled.div`
	padding: 36px 28px 16px;
	text-align: center;
	flex: 1;
	min-height: 0;

	${media.phone} {
		padding: 24px 16px 12px;
	}
`

export const CardFooter = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 12px;
	padding: 16px 24px;
	border-top: 1px solid ${border1};
	background: ${vanilla};
	flex-wrap: wrap;

	${media.phone} {
		padding: 12px 14px;
	}
`

export const FooterHint = styled.div`
	font-size: ${fontSizes.micro};
	color: ${textMuted};
	flex: 1;
	min-width: 200px;

	${media.phone} {
		min-width: 0;
		flex-basis: 100%;
	}
`

export const FooterActions = styled.div`
	display: flex;
	gap: 10px;
	flex-shrink: 0;
`

/**
 * The CTA the two wizard-style pages share, this one and the create-trip flow:
 * the large Button at one minimum width, so Next, Skip, Build itinerary and
 * Try again read as the same control wherever a card's bottom bar shows one.
 */
export const FlowCtaButton = styled(Button).attrs({ size: 'lg' as const })`
	min-width: 150px;
`

/** Footer CTA: the warm primary moves the import forward, the cool one retries or submits. */
export const PrimaryButton = styled(FlowCtaButton).attrs<{ $variant?: 'cool' | 'warm' }>(({ $variant }) => ({
	variant: $variant === 'warm' ? ('primary' as const) : ('cool' as const),
}))<{ $variant?: 'cool' | 'warm'; $disabled?: boolean }>`
	${({ $disabled }) =>
		$disabled &&
		css`
			opacity: 0.45;
			cursor: not-allowed;
		`}
`

/** The quiet one beside it, at the same height. */
export const GhostButton = styled(Button).attrs({ variant: 'secondary' as const, size: 'lg' as const })``

/* ─── Trip context strip ─── */

export const TripStrip = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 12px 16px;
	background: ${blue}0f;
	border-radius: ${radii.inner};
	margin-bottom: 16px;
	flex-wrap: wrap;
`

export const TripStripIcon = styled.div`
	width: 36px;
	height: 36px;
	border-radius: ${radii.small};
	background: ${blue};
	color: ${white};
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
`

export const TripStripLabel = styled.div`
	${text.eyebrow}
	color: ${orange};
`

export const TripStripName = styled.div`
	${text.labelSm}
	color: ${black};
	margin-top: 2px;
	overflow-wrap: anywhere;
`

export const TripStripDates = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	font-size: ${fontSizes.micro};

	span:first-child {
		font-weight: 700;
		color: ${black};
	}
	span:last-child {
		color: ${textMuted};
	}
`

/* ─── Drop zone ─── */

export const DropZoneRoot = styled.div<{ $state: 'idle' | 'dragOver' }>`
	position: relative;
	border-radius: ${radii.card};
	background: ${backgroundOrange};
	padding: 36px 28px 24px;
	text-align: center;
	overflow: hidden;
	transition: all ${motion.base};
	min-height: 240px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;

	${media.phone} {
		padding: 24px 14px 18px;
		min-height: 200px;
	}

	${({ $state }) =>
		$state === 'idle'
			? css`
					border: 1.5px dashed ${border2};
				`
			: css`
					background: ${orange}0f;
					border: 2px dashed ${orange};
					box-shadow: 0 8px 24px ${orange}33;
				`}
`

export const DropIconWrap = styled.div<{ $active: boolean }>`
	width: 56px;
	height: 56px;
	border-radius: ${radii.inner};
	display: flex;
	align-items: center;
	justify-content: center;
	margin-bottom: 12px;
	background: ${({ $active }) => ($active ? orange : `${orange}1a`)};
	color: ${({ $active }) => ($active ? white : darkOrange)};
	transition: all ${motion.base};
`

export const DropTitle = styled.div<{ $active?: boolean }>`
	${text.title}
	margin-bottom: 6px;
	color: ${({ $active }) => ($active ? darkOrange : black)};
`

export const DropHint = styled.div`
	font-size: ${fontSizes.small};
	color: ${textMuted};
	max-width: 380px;
	margin: 0 auto 16px;
`

export const BrowseButton = styled(Button).attrs({ variant: 'ghost' as const })`
	padding: 0;
	font-size: inherit;
	text-decoration: underline;
`

export const DropLimits = styled.div`
	font-size: ${fontSizes.micro};
	color: ${darkGrey};
`

/* ─── File list panel ─── */

export const FileListPanel = styled.div`
	background: ${white};
	border-radius: ${radii.inner};
	border: 1px solid ${border1};
	padding: 14px;
	margin-bottom: 12px;
`

export const FileListHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	${text.eyebrow}
	color: ${darkOrange};
	margin-bottom: 10px;

	span:last-child {
		color: ${darkGrey};
		font-weight: 500;
	}
`

export const FileRowRoot = styled.div<{ $error?: boolean; $kind: 'pdf' | 'image' }>`
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 10px 12px;
	border-radius: ${radii.inner};
	border: 1px solid;
	margin-bottom: 8px;

	${({ $error, $kind }) =>
		$error
			? css`
					background: ${red}1a;
					border-color: ${red}73;
				`
			: $kind === 'image'
				? css`
						background: ${blue}0f;
						border-color: ${blue}2e;
					`
				: css`
						background: ${orange}0f;
						border-color: ${orange}33;
					`}
`

export const FileIconBox = styled.div<{ $error?: boolean; $kind: 'pdf' | 'image' }>`
	width: 36px;
	height: 36px;
	border-radius: ${radii.small};
	background: ${white};
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	color: ${({ $error, $kind }) =>
		$error ? danger : $kind === 'image' ? blue : darkOrange};
`

export const FileInfo = styled.div`
	flex: 1;
	min-width: 0;
`

export const FileName = styled.div<{ $error?: boolean }>`
	font-size: ${fontSizes.small};
	font-weight: 700;
	color: ${({ $error }) => ($error ? danger : black)};
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`

export const FileMeta = styled.div`
	font-size: ${fontSizes.micro};
	color: ${textMuted};
	display: flex;
	align-items: center;
	gap: 6px;
	margin-top: 2px;
	flex-wrap: wrap;
`

export const FileMetaDot = styled.span`
	color: ${darkGrey};
`

export const FileMetaError = styled.span`
	color: ${danger};
	font-weight: 700;
`

export const FileMetaReady = styled.span`
	color: ${success};
	font-weight: 700;
`

export const FileRemoveButton = styled(Button).attrs({ variant: 'icon' as const })`
	width: 28px;
	height: 28px;
`

export const AddMoreButton = styled.button`
	${dashedSlot}
	margin-top: 4px;
	font-weight: 700;
`

/* ─── Notes ─── */

export const NotesWrap = styled.div`
	margin-top: 16px;
	padding-bottom: 16px;
`

export const NotesLabel = styled.label`
	display: block;
	${text.eyebrow}
	color: ${textMuted};
	margin-bottom: 8px;
`

export const NotesArea = styled(Textarea)`
	min-height: 60px;
`

/* ─── Validation alert banner ─── */

export const AlertBanner = styled.div`
	background: ${red}14;
	border: 1px solid ${red}59;
	border-radius: ${radii.inner};
	padding: 12px 14px;
	margin-bottom: 14px;
	display: flex;
	align-items: flex-start;
	gap: 12px;
`

export const AlertIcon = styled.div`
	width: 32px;
	height: 32px;
	border-radius: ${radii.small};
	background: ${red};
	color: ${white};
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
`

export const AlertTitle = styled.div`
	${text.labelSm}
	color: ${black};
`

export const AlertSub = styled.div`
	font-size: ${fontSizes.caption};
	color: ${textMuted};
	margin-top: 2px;
`

/* ─── Status plates (no_events, failed, rate_limit, stuck) ─── */

export const Plate = styled.div<{ $tone: 'orange' | 'red' | 'blue' | 'gold' }>`
	width: 88px;
	height: 88px;
	border-radius: 50%;
	margin: 0 auto 20px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: ${({ $tone }) =>
		$tone === 'red' ? red : $tone === 'blue' ? blue : darkOrange};
	background: ${({ $tone }) =>
		$tone === 'red'
			? `${red}24`
			: $tone === 'blue'
				? `${blue}1a`
				: $tone === 'gold'
					? `${gold}2e` /* ${gold} at 18% */
					: `${orange}1a`};
`

export const StateHeadline = styled.div`
	${text.heading}
	color: ${blue};
	margin-bottom: 8px;
`

export const StateParagraph = styled.div`
	font-size: ${fontSizes.base};
	color: ${textMuted};
	max-width: 440px;
	margin: 0 auto 18px;
	line-height: ${leading.body};
`

export const InlinePanel = styled.div`
	text-align: left;
	background: ${white};
	border: 1px solid ${border1};
	border-radius: ${radii.inner};
	padding: 12px 16px;
	max-width: 440px;
	margin: 0 auto 20px;
`

export const InlinePanelLabel = styled.div`
	${text.eyebrow}
	color: ${orange};
	margin-bottom: 8px;
`

export const InlinePanelMutedLabel = styled.div`
	${text.eyebrow}
	color: ${darkGrey};
	margin-bottom: 6px;
`

export const ErrorRefBlock = styled.div`
	font-family: ${fontMono};
	font-size: ${fontSizes.micro};
	color: ${textMuted};
`

export const TipsList = styled.ul`
	margin: 0;
	padding-left: 18px;
	font-size: ${fontSizes.caption};
	color: ${black};
	line-height: ${leading.body};
`

/* ─── Rate limit gauge ─── */

export const RateLimitPanel = styled.div`
	background: ${white};
	border: 1px solid ${border1};
	border-radius: ${radii.inner};
	padding: 14px 18px;
	max-width: 420px;
	margin: 0 auto 12px;
	display: flex;
	align-items: center;
	gap: 14px;
	text-align: left;
`

export const GaugeColumn = styled.div`
	flex: 1;
`

export const GaugeLabel = styled.div`
	font-size: ${fontSizes.caption};
	color: ${textMuted};
	margin-bottom: 6px;
`

export const GaugeTrack = styled.div`
	height: 8px;
	border-radius: ${radii.round};
	background: ${grey};
	overflow: hidden;
	position: relative;
`

export const GaugeFill = styled.div<{ $pct: number }>`
	height: 100%;
	width: ${({ $pct }) => Math.min(100, Math.max(0, $pct))}%;
	background: linear-gradient(90deg, ${orange} 0%, ${darkOrange} 100%);
	border-radius: ${radii.round};
`

export const GaugeCount = styled.div`
	${text.heading}
	font-weight: 900;
	color: ${blue};
	min-width: 60px;
	text-align: right;
`

/* ─── Processing state ─── */

export const ProcessingWrap = styled.div`
	padding: 32px 24px 16px;
	text-align: center;
`

export const ProcessingHeading = styled.div`
	${text.heading}
	color: ${blue};
	margin-bottom: 8px;
`

export const ProcessingSub = styled.div`
	font-size: ${fontSizes.small};
	color: ${textMuted};
	margin: 0 auto 24px;
	max-width: 380px;
`

export const StepsList = styled.div`
	text-align: left;
	max-width: 420px;
	margin: 0 auto;
`

export const StepRow = styled.div<{ $opacity: number }>`
	display: flex;
	align-items: flex-start;
	gap: 12px;
	padding: 10px 0;
	opacity: ${({ $opacity }) => $opacity};
	transition: opacity ${motion.base};
`

export const StepBullet = styled.div<{ $state: 'done' | 'active' | 'pending' }>`
	width: 22px;
	height: 22px;
	border-radius: 50%;
	background: ${({ $state }) =>
		$state === 'done' ? success : $state === 'active' ? orange : grey};
	color: ${white};
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	margin-top: 1px;
`

export const StepActiveDot = styled.div`
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background: ${white};
	animation: ${pulseDot} 0.6s ease-in-out infinite alternate;
`

export const StepLabel = styled.div`
	${text.labelSm}
	color: ${black};
`

export const StepDetail = styled.div`
	font-size: ${fontSizes.caption};
	color: ${textMuted};
	margin-top: 1px;
`

/* ─── Orbit Loader ─── */

export const LoaderWrap = styled.div<{ $size: number }>`
	position: relative;
	width: ${({ $size }) => $size}px;
	height: ${({ $size }) => $size}px;
	margin: 0 auto 22px;
`

export const LoaderRingOuter = styled.div`
	position: absolute;
	inset: 0;
	border: 2px dashed ${orange}66;
	border-radius: 50%;
	animation: ${orbitSpin} 6s linear infinite;
`

export const LoaderRingInner = styled.div`
	position: absolute;
	inset: 14%;
	border: 1.5px dashed ${blue}4d;
	border-radius: 50%;
	animation: ${orbitSpinReverse} 9s linear infinite;
`

export const LoaderCore = styled.div<{ $size: number }>`
	position: absolute;
	left: 50%;
	top: 50%;
	width: ${({ $size }) => $size * 0.4}px;
	height: ${({ $size }) => $size * 0.4}px;
	margin-left: ${({ $size }) => -($size * 0.2)}px;
	margin-top: ${({ $size }) => -($size * 0.2)}px;
	border-radius: 50%;
	background: ${orange};
	box-shadow: 0 8px 22px ${orange}73,
		inset -4px -4px 12px ${black}2e;
`

export const LoaderSatellite = styled.div<{ $size: number }>`
	position: absolute;
	left: 50%;
	top: 0;
	width: 12px;
	height: 12px;
	margin-left: -6px;
	border-radius: 50%;
	background: ${blue};
	transform-origin: 6px ${({ $size }) => $size / 2}px;
	animation: ${orbitSpin} 3s linear infinite;
	box-shadow: 0 4px 12px ${blue}73;
`

/* ─── Success ─── */

export const SuccessBanner = styled.div`
	background: linear-gradient(135deg, ${blue}14, ${orange}14);
	border: 1px solid ${blue}1f;
	border-radius: ${radii.inner};
	padding: 16px 18px;
	display: flex;
	align-items: center;
	gap: 14px;
	margin-bottom: 18px;
`

export const SuccessCheckChip = styled.div`
	width: 44px;
	height: 44px;
	border-radius: ${radii.inner};
	background: ${success};
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	color: ${white};
`

export const SuccessHeadline = styled.div`
	${text.title}
	color: ${blue};
`

export const SuccessSub = styled.div`
	font-size: ${fontSizes.caption};
	color: ${textMuted};
	margin-top: 2px;
`

export const SuccessBannerText = styled.div`
	flex: 1;
	min-width: 0;
`

/** The imported events, grouped by day. Keeps the card body's air below it, like the notes do on the idle body. */
export const EventListPanel = styled.div`
	background: ${white};
	border-radius: ${radii.inner};
	border: 1px solid ${border1};
	overflow: hidden;
	margin-bottom: 16px;
`

export const DayGroup = styled.div<{ $first: boolean }>`
	border-top: ${({ $first }) => ($first ? 'none' : `1px solid ${border1}`)};
`

export const DayHeader = styled.div`
	padding: 10px 16px;
	background: ${vanilla};
	${text.eyebrow}
	color: ${blackOrange};
`

export const EventRow = styled.div<{ $first: boolean; $idx: number }>`
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 12px 16px;
	border-top: ${({ $first }) => ($first ? 'none' : `1px solid ${border1}`)};
	animation: ${stagger} 200ms ease-out backwards;
	animation-delay: ${({ $idx }) => $idx * 30}ms;
`

export const EventInfo = styled.div`
	flex: 1;
	min-width: 0;
`

export const EventName = styled.div`
	${text.labelSm}
	color: ${black};
`

export const EventLocation = styled.div`
	font-size: ${fontSizes.micro};
	color: ${textMuted};
	display: flex;
	align-items: center;
	gap: 4px;
	margin-top: 2px;

	svg {
		color: ${darkOrange};
		flex-shrink: 0;
	}
`

export const EventNoLocation = styled.div`
	font-size: ${fontSizes.micro};
	color: ${darkGrey};
	margin-top: 2px;
`

export const EventTime = styled.div`
	font-size: ${fontSizes.caption};
	color: ${textMuted};
	min-width: 50px;
	text-align: right;
`

export const EventDeleteButton = styled(Button).attrs({ variant: 'icon' as const })`
	width: 28px;
	height: 28px;
	color: ${darkGrey};

	&:hover:not(:disabled) {
		background: ${red}1f;
		color: ${danger};
	}
`

export const RemoveAllButton = styled(Button).attrs({ variant: 'ghost' as const, size: 'sm' as const })`
	padding: 6px 8px;
	color: ${danger};

	&:hover:not(:disabled) {
		background: ${red}1a;
	}
`

export const SmallGhostButton = styled(Button).attrs({ variant: 'ghost' as const, size: 'sm' as const })`
	padding: 6px 8px;
	color: ${textMuted};
`

export const RemoveAllConfirm = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
	flex-shrink: 0;
`

export const StrongAccent = styled.strong`
	color: ${black};
`

